/**
 * SavingsBreakdownWidget
 * 
 * Shows a real-time breakdown of token savings by source:
 * Prompt Compression, Context Optimization, Memory Optimization, Smart Model Routing, Semantic Caching.
 * Displayed in the dashboard right panel.
 */

import { useMemo } from 'react';
import { useDashboardStore } from '../store/dashboardStore';
import type { SavingsBreakdown, SavingsSourceStats } from '../store/dashboardStore';

interface SourceRow {
  key: keyof Omit<SavingsBreakdown, 'total' | 'sessionStartedAt'>;
  label: string;
  emoji: string;
  color: string;
  bgColor: string;
}

const SOURCES: SourceRow[] = [
  { key: 'clipboard',         label: 'Prompt Compression',   emoji: '📋', color: '#22d3ee',  bgColor: 'rgba(34,211,238,0.1)' },
  { key: 'contextSqueeze',    label: 'Context Optimization', emoji: '🗜️',  color: '#a78bfa',  bgColor: 'rgba(167,139,250,0.1)' },
  { key: 'historyPrune',      label: 'Memory Optimization',  emoji: '📜', color: '#fb923c',  bgColor: 'rgba(251,146,60,0.1)' },
  { key: 'complexityRouting', label: 'Smart Model Routing',  emoji: '🔀', color: '#34d399',  bgColor: 'rgba(52,211,153,0.1)' },
  { key: 'cache',             label: 'Semantic Caching',     emoji: '⚡', color: '#fbbf24',  bgColor: 'rgba(251,191,36,0.1)' },
];

function formatTokens(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toString();
}

function formatUSD(n: number): string {
  if (n === 0) return '$0.0000';
  if (n < 0.0001) return '<$0.0001';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(n);
}

interface SourceBarProps {
  stats: SavingsSourceStats;
  maxTokens: number;
  color: string;
  bgColor: string;
  label: string;
  emoji: string;
}

function SourceBar({ stats, maxTokens, color, bgColor, label, emoji }: SourceBarProps) {
  const pct = maxTokens > 0 ? Math.min(100, (stats.tokensSaved / maxTokens) * 100) : 0;
  const active = stats.tokensSaved > 0;

  return (
    <div
      style={{
        padding: '8px 10px',
        borderRadius: '8px',
        backgroundColor: active ? bgColor : 'rgba(255,255,255,0.02)',
        border: `1px solid ${active ? color + '30' : 'rgba(255,255,255,0.04)'}`,
        marginBottom: '6px',
        transition: 'all 0.3s ease',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: active ? color : '#64748b', display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span>{emoji}</span>
          <span>{label}</span>
        </span>
        <span
          style={{
            fontSize: '10px',
            fontWeight: 700,
            color: active ? color : 'rgba(16,185,129,0.6)',
            letterSpacing: active ? 'normal' : '0.05em',
            textTransform: active ? 'none' : 'uppercase',
          }}
        >
          {active ? `${formatTokens(stats.tokensSaved)} tkns` : 'Monitoring'}
        </span>
      </div>

      {/* Progress bar */}
      <div style={{ height: '3px', borderRadius: '2px', backgroundColor: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            backgroundColor: color,
            borderRadius: '2px',
            transition: 'width 0.6s ease',
            boxShadow: active ? `0 0 6px ${color}80` : 'none',
          }}
        />
      </div>
    </div>
  );
}

export function SavingsBreakdownWidget() {
  const breakdown = useDashboardStore((state) => state.savingsBreakdown);
  const metrics = useDashboardStore((state) => state.metrics);

  const displayBreakdown = useMemo(() => {
    if (!breakdown) return null;
    
    // If breakdown statistics are 0 but global metrics contains cost Saved,
    // distribute the accumulated values to Semantic Caching for seamless alignment.
    if (breakdown.total.tokensSaved === 0 && metrics && metrics.tokensSaved > 0) {
      const copied = JSON.parse(JSON.stringify(breakdown)) as SavingsBreakdown;
      copied.cache.tokensSaved = metrics.tokensSaved;
      copied.cache.costSaved = metrics.costSaved;
      copied.total.tokensSaved = metrics.tokensSaved;
      copied.total.costSaved = metrics.costSaved;
      return copied;
    }
    return breakdown;
  }, [breakdown, metrics]);

  if (!displayBreakdown) {
    return (
      <div style={{ padding: '14px 16px' }}>
        <div style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', marginBottom: '10px', textTransform: 'uppercase' }}>
          💰 Savings Breakdown
        </div>
        <div style={{ fontSize: '11px', color: '#475569', textAlign: 'center', padding: '16px 0' }}>
          Waiting for first request…
        </div>
      </div>
    );
  }

  const total = displayBreakdown.total;
  const maxTokens = Math.max(
    displayBreakdown.clipboard.tokensSaved,
    displayBreakdown.contextSqueeze.tokensSaved,
    displayBreakdown.historyPrune.tokensSaved,
    displayBreakdown.complexityRouting.tokensSaved,
    displayBreakdown.cache.tokensSaved,
    1
  );



  return (
    <div style={{ padding: '14px 16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            💰 Savings Breakdown
          </div>
          <div style={{ fontSize: '10px', color: '#10b981', fontWeight: 'bold', marginTop: '1px' }}>
            Lifetime Savings · Synced
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div
            style={{
              fontSize: '16px',
              fontWeight: 800,
              color: total.tokensSaved > 0 ? '#22d3ee' : '#475569',
              lineHeight: 1,
              textShadow: total.tokensSaved > 0 ? '0 0 12px rgba(34,211,238,0.4)' : 'none',
            }}
          >
            {formatUSD(total.costSaved)}
          </div>
          <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>
            {formatTokens(total.tokensSaved)} total tokens
          </div>
        </div>
      </div>

      {/* Source rows */}
      <div>
        {SOURCES.map(({ key, label, emoji, color, bgColor }) => (
          <SourceBar
            key={key}
            stats={displayBreakdown[key]}
            maxTokens={maxTokens}
            label={label}
            emoji={emoji}
            color={color}
            bgColor={bgColor}
          />
        ))}
      </div>

      {/* Footer note */}
      {total.events > 0 && (
        <div style={{ fontSize: '10px', color: '#475569', textAlign: 'center', marginTop: '6px' }}>
          {total.events} savings event{total.events !== 1 ? 's' : ''} recorded
        </div>
      )}
    </div>
  );
}
